import React, { useState, useEffect, useCallback } from 'react'
import connexion from '../../connexion'

/*
** Activation des notifications push pour CET appareil (abonnement navigateur).
** Autonome : détecte le support, gère permission/abonnement, resynchronise.
** États : unavailable (pas de service worker — dev ou navigateur non supporté),
** denied (bloquées navigateur), off, on, loading.
*/
function urlBase64ToUint8Array(base64String) {
	const padding = '='.repeat((4 - base64String.length % 4) % 4)
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
	const rawData = window.atob(base64)
	const outputArray = new Uint8Array(rawData.length)
	for (let i = 0; i < rawData.length; ++i)
		outputArray[i] = rawData.charCodeAt(i)
	return outputArray
}

export default function PushSubscriptionToggle({ userId }) {
	const [status, setStatus] = useState('unavailable')

	useEffect(() => {
		if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
			return
		if (Notification.permission === 'denied')
		{
			setStatus('denied')
			return
		}
		navigator.serviceWorker.getRegistration()
			.then(registration => {
				if (!registration)
					return
				return registration.pushManager.getSubscription()
					.then(subscription => setStatus(subscription ? 'on' : 'off'))
			})
			.catch(() => {})
	}, [])

	const enable = useCallback(async () => {
		try
		{
			setStatus('loading')
			const permission = await Notification.requestPermission()
			if (permission !== 'granted')
			{
				setStatus(permission === 'denied' ? 'denied' : 'off')
				return
			}
			const registration = await navigator.serviceWorker.ready
			const keyRet = await fetch(connexion.connect.url + 'dashboard/push/public-key').then(r => r.json())
			const subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(keyRet.publicKey)
			})
			const ret = await fetch(connexion.connect.url + 'dashboard/push/subscribe', {
				method: 'POST',
				body: JSON.stringify({ user: userId, subscription: subscription })
			}).then(r => r.json())
			setStatus(ret === false ? 'off' : 'on')
		}
		catch (err)
		{
			console.log('push subscribe failed', err)
			setStatus('off')
		}
	}, [userId])

	const disable = useCallback(async () => {
		try
		{
			setStatus('loading')
			const registration = await navigator.serviceWorker.getRegistration()
			const subscription = registration ? await registration.pushManager.getSubscription() : null
			if (subscription)
			{
				const auth = subscription.toJSON().keys.auth
				await subscription.unsubscribe()
				await fetch(connexion.connect.url + 'dashboard/push/unsubscribe', {
					method: 'POST',
					body: JSON.stringify({ auth: auth })
				})
			}
			setStatus('off')
		}
		catch (err)
		{
			console.log('push unsubscribe failed', err)
			setStatus('off')
		}
	}, [])

	if (status === 'unavailable')
		return (
			<p className="text-xs text-slate-400 font-semibold my-0">
				Non disponible sur ce navigateur. Sur téléphone, installez d'abord l'application
				sur l'écran d'accueil (iPhone : iOS 16.4 minimum).
			</p>
		)

	if (status === 'denied')
		return (
			<p className="text-xs text-amber-700 font-semibold my-0">
				Les notifications sont bloquées pour ce site dans les réglages de votre navigateur —
				réautorisez-les puis revenez ici.
			</p>
		)

	return (
		<div className="flex items-center justify-between gap-4">
			<p className="text-xs font-semibold my-0" style={{ color: status === 'on' ? '#166534' : '#64748b' }}>
				{status === 'on'
					? 'Activées sur cet appareil : vous serez alerté des interventions qui vous concernent.'
					: 'Recevez une alerte quand une intervention vous est assignée ou vous concerne.'}
			</p>
			<div
				role="switch"
				aria-checked={status === 'on'}
				aria-label="Notifications push sur cet appareil"
				tabIndex={0}
				onClick={status === 'loading' ? undefined : (status === 'on' ? disable : enable)}
				onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && status !== 'loading') { e.preventDefault(); (status === 'on' ? disable : enable)() } }}
				style={{ cursor: status === 'loading' ? 'wait' : 'pointer', flexShrink: 0 }}
			>
				<div
					style={{
						width: '2.75rem',
						height: '1.5rem',
						borderRadius: '9999px',
						backgroundColor: status === 'on' ? '#2563eb' : '#d1d5db',
						transition: 'background-color 0.2s',
						position: 'relative',
						opacity: status === 'loading' ? 0.6 : 1,
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: '2px',
							left: status === 'on' ? 'calc(100% - 1.25rem - 2px)' : '2px',
							width: '1.25rem',
							height: '1.25rem',
							borderRadius: '50%',
							backgroundColor: '#fff',
							transition: 'left 0.2s',
							boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
						}}
					/>
				</div>
			</div>
		</div>
	)
}
